import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Youtube, ArrowUp, ArrowDown, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

export default function AdminVideos() {
  useSeo({ title: "Admin — Home Video Carousel", noindex: true });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ url: "", title: "", pages: ["home"] });
  const [busy, setBusy] = useState(false);
  const [videoPages, setVideoPages] = useState([{ key: "home", label: "Home page" }]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, pagesRes] = await Promise.all([
        api.get("/admin/home-videos"),
        api.get("/video-pages").catch(() => ({ data: null })),
      ]);
      setItems(data);
      if (pagesRes.data) setVideoPages(pagesRes.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const togglePage = (key, current, setter, isForm = false) => {
    const next = current.includes(key) ? current.filter((p) => p !== key) : [...current, key];
    if (isForm) setForm({ ...form, pages: next.length ? next : ["home"] });
    return next.length ? next : ["home"];
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) { toast.error("Paste a YouTube URL or 11-character video ID"); return; }
    setBusy(true);
    try {
      // Insert at the top: use a negative order lower than any existing one.
      const minOrder = items.length ? Math.min(...items.map((i) => i.order || 0)) : 0;
      await api.post("/admin/home-videos", {
        type: "youtube", url: form.url.trim(), title: form.title.trim(),
        order: minOrder - 1, active: true, pages: form.pages,
      });
      toast.success(`Added to ${form.pages.length === 1 ? form.pages[0] : form.pages.length + ' pages'} — now first in the carousel`);
      setForm({ url: "", title: "", pages: ["home"] });
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/home-videos/${id}`, patch);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this video from the carousel?")) return;
    try {
      await api.delete(`/admin/home-videos/${id}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx], b = items[target];
    await update(a.id, { order: b.order });
    await update(b.id, { order: a.order });
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="admin-videos-add-form">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Youtube size={18} className="text-red-600" /> Add YouTube video to top of carousel</h2>
        <p className="text-sm text-slate-500">Paste any YouTube URL — full video, Short, or youtu.be short link. We extract the 11-char ID automatically.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">YouTube URL or ID</span>
            <input data-testid="videos-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://youtube.com/shorts/ArK0E4ClZmM"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Title (optional)</span>
            <input data-testid="videos-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder='e.g. "New demo"'
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
        </div>
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Show on pages</span>
          <div className="mt-1.5 flex flex-wrap gap-2" data-testid="videos-pages-checkboxes">
            {videoPages.map((p) => (
              <label key={p.key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-bold transition-colors ${
                form.pages.includes(p.key) ? "bg-emerald-50 border-emerald-500 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}>
                <input type="checkbox"
                  data-testid={`videos-page-${p.key}`}
                  checked={form.pages.includes(p.key)}
                  onChange={() => togglePage(p.key, form.pages, null, true)}
                  className="w-3.5 h-3.5 accent-emerald-600" />
                {p.label}
              </label>
            ))}
          </div>
        </div>
        <button data-testid="videos-add-btn" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0B0F0D] text-[#D4AF37] font-bold px-5 py-2.5 rounded-xl hover:bg-[#1a2420] disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {busy ? "Adding…" : "Add to top"}
        </button>
      </form>

      <div>
        <h2 className="font-display text-lg font-bold mb-3">Current carousel ({items.length})</h2>
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">
            No videos yet. The home page is showing the default built-in list. Add one above to override.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-videos-list">
            {items.map((v, idx) => (
              <li key={v.id} className={`bg-white border rounded-xl p-3 flex items-center gap-3 ${v.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}
                  data-testid={`videos-item-${v.id}`}>
                {v.type === "youtube" ? (
                  <img src={`https://img.youtube.com/vi/${v.video_id}/mqdefault.jpg`} alt={v.title || v.video_id}
                    className="w-28 aspect-video object-cover rounded-md bg-black" loading="lazy" />
                ) : (
                  <div className="w-28 aspect-video rounded-md bg-slate-900 text-white text-xs flex items-center justify-center">MP4</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{v.title || (v.type === "youtube" ? `/${v.video_id}` : v.src)}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    #{idx + 1} · order {v.order} · {v.type}
                    {v.type === "youtube" && <> · <a className="underline" target="_blank" rel="noreferrer" href={`https://youtu.be/${v.video_id}`}>open</a></>}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {videoPages.map((p) => {
                      const on = (v.pages || ["home"]).includes(p.key);
                      return (
                        <button key={p.key} onClick={() => {
                          const next = on ? (v.pages || ["home"]).filter((x) => x !== p.key) : [...(v.pages || ["home"]), p.key];
                          update(v.id, { pages: next.length ? next : ["home"] });
                        }}
                          data-testid={`videos-row-page-${v.id}-${p.key}`}
                          className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border transition-colors ${
                            on ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-slate-400 border-slate-200 hover:bg-slate-50"
                          }`}
                          title={on ? `Showing on ${p.label} — click to remove` : `Not on ${p.label} — click to add`}>
                          {on ? "✓ " : ""}{p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`videos-up-${v.id}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move up"><ArrowUp size={16} /></button>
                  <button data-testid={`videos-down-${v.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move down"><ArrowDown size={16} /></button>
                  <button data-testid={`videos-toggle-${v.id}`} onClick={() => update(v.id, { active: !v.active })}
                    className="p-1.5 rounded hover:bg-slate-100" aria-label="Toggle visibility">
                    {v.active ? <Eye size={16} /> : <EyeOff size={16} className="text-slate-400" />}
                  </button>
                  <button data-testid={`videos-delete-${v.id}`} onClick={() => remove(v.id)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500" aria-label="Delete"><Trash2 size={16} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
